/*
 * Chi e' questa clinica, per le finestre della modulistica.
 *
 * PERCHE' ESISTE
 * Fino al 29/10/2026 le finestre di accettazione, di firma dei consensi e
 * di completamento disegnavano in testa l'intestazione di Rimedical scritta
 * a mano (ragione sociale, indirizzo, telefoni, P.IVA), e la tela della
 * firma metteva sotto il comune di Rimedical. Su un'installazione di Coorte
 * il paziente avrebbe firmato davanti al nome di un'altra clinica.
 *
 * I dati arrivano da /api/struttura, la stessa risposta che il programma
 * chiede all'avvio per la palette (utils/palette.js la consegna qui con
 * `ricordaStruttura`). Ma quella risposta l'intestazione (indirizzo,
 * telefoni, P.IVA, codice univoco, sito, posta) non ce l'ha: il server la
 * da' solo a chi e' della clinica, cioe' con la sessione nel gestionale o
 * con la chiave del tablet sul tablet. Quindi la prima finestra che la
 * disegna la richiede una volta, con quelle credenziali, e da li' resta in
 * memoria. Con le stesse credenziali non si richiede: se il server non la
 * da' (sessione scaduta, tablet disattivato) l'intestazione resta vuota.
 *
 * Il ripiego e' neutro: se il server non risponde l'intestazione e' vuota e
 * sotto la firma resta la sola data. Mai i dati di un'altra clinica.
 */
import { useEffect, useState } from 'react';
import intestazioniTablet, { CHIAVE_SALVATA_TABLET } from './chiaveDispositivo';

let ricordo = null;
let inCorso = null;
let chiestaCon = null;
const ascoltatori = new Set();

/** Tiene da parte la risposta di /api/struttura (il campo `data`). Una
    risposta senza intestazione (quella della palette, chiesta senza
    credenziali) non toglie quella gia' avuta. */
export function ricordaStruttura(dati) {
    if (!dati || typeof dati !== 'object') return;
    ricordo = (!dati.intestazione && ricordo && ricordo.intestazione)
        ? { ...dati, intestazione: ricordo.intestazione }
        : dati;
    ascoltatori.forEach((f) => f(ricordo));
}

/* Con che cosa ci si presenta: sul tablet la sua chiave (nell'intestazione,
   mai nell'indirizzo), nel gestionale la sessione, come services/api.js. */
function credenziali() {
    try {
        if (window.location.pathname.startsWith('/tablet')) {
            const chiave = window.localStorage.getItem(CHIAVE_SALVATA_TABLET);
            return chiave ? intestazioniTablet(chiave) : null;
        }
        const token = window.localStorage.getItem('token');
        return token ? { Authorization: `Bearer ${token}` } : null;
    } catch (e) {
        return null;
    }
}

/** La struttura gia' vista, o null. */
export function strutturaRicordata() {
    return ricordo;
}

function segnoDi(testo) {
    let n = 5381;
    for (let i = 0; i < testo.length; i += 1) n = ((n * 33) ^ testo.charCodeAt(i)) >>> 0;
    return String(n);
}

function chiedi() {
    if (ricordo && ricordo.intestazione) return Promise.resolve(ricordo);
    const h = credenziali();
    /* Un segno delle credenziali, non il loro valore: cambiate (un login nuovo,
       una chiave riscritta sul tablet) si richiede. */
    const segno = h ? segnoDi(Object.keys(h).join(',') + ':' + Object.values(h).join('')) : 'nessuna';
    if (ricordo && chiestaCon === segno) return Promise.resolve(ricordo);
    if (!inCorso) {
        chiestaCon = segno;
        inCorso = fetch('/api/struttura', h ? { headers: h } : undefined)
            .then((r) => r.json())
            .then((d) => { ricordaStruttura(d?.data); return ricordo; })
            .catch(() => null)
            .finally(() => { inCorso = null; });
    }
    return inCorso;
}

/** I dati della struttura in un componente: null finche' non arrivano. */
export function useStruttura() {
    const [s, setS] = useState(ricordo);
    useEffect(() => {
        ascoltatori.add(setS);
        if (ricordo) setS(ricordo);
        if (!ricordo || !ricordo.intestazione) chiedi();
        return () => { ascoltatori.delete(setS); };
    }, []);
    return s;
}

const testo = (v) => (typeof v === 'string' ? v.trim() : '');

/**
 * Le righe dell'intestazione, nella forma di prima:
 * indirizzo – CAP comune (provincia) / Tel. – Cell. / P.IVA – Cod. Univoco,
 * e con `contatti` anche sito – posta. Una riga senza dati non c'e'.
 */
export function righeIntestazione(s, { contatti = false } = {}) {
    const i = (s && s.intestazione) || {};
    const unisci = (parti) => parti.filter(Boolean).join(' – ');
    const prov = testo(i.provincia);
    const comune = [testo(i.cap), testo(i.citta), prov && `(${prov})`].filter(Boolean).join(' ');
    const righe = [
        unisci([testo(i.indirizzo), comune]),
        unisci([testo(i.telefono) && `Tel. ${testo(i.telefono)}`,
            testo(i.cellulare) && `Cell. ${testo(i.cellulare)}`]),
        unisci([testo(i.piva) && `P.IVA ${testo(i.piva)}`,
            testo(i.codice_univoco) && `Cod. Univoco ${testo(i.codice_univoco)}`]),
    ];
    if (contatti) righe.push(unisci([testo(i.sito), testo(i.email)]));
    return righe.filter(Boolean);
}

/** La ragione sociale, o vuoto. */
export function ragioneSociale(s) {
    return testo(s && s.ragione_sociale);
}

/** «Comune, data» sotto la firma; senza comune, la sola data. */
export function luogoEData(s, quando = new Date()) {
    const citta = testo(s && s.intestazione && s.intestazione.citta);
    return [citta, quando.toLocaleDateString('it-IT')].filter(Boolean).join(', ');
}
